import React, { ChangeEvent, FC, ReactElement, useRef, useState } from "react";
import { Button, Icon, Images, Textarea } from "@shared/components";
import { useDispatch, useSelector } from "react-redux";
import { actions as PayPerLeadActions, selectors as PayPerLeadSelectors } from "@containers/PayPerLead/store";
import { getActiveAccount } from "@containers/Account/store/selectors";
import { showNotification } from "@shared/store/actions";

import "./index.scss";

interface ModalPurchaseLeadProps {
  heading?: string;
  content?: ReactElement | string;
  onClose: () => void;
}

const MAX_LENGTH = 250;
const MAX_FILES_LENGTH = 10;
const MAX_FILE_SIZE = 5; // mb

const ModalDisputeLead: FC<ModalPurchaseLeadProps> = ({ heading, content, onClose }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const dispatch = useDispatch();
  const ref = useRef<HTMLInputElement>(null);

  const handleClick = () => ref.current?.click?.();

  const addImage = (e: ChangeEvent<HTMLInputElement>) => {
    const sizeExceeds = [];
    const list = [...(e.target.files || [])].slice(0, MAX_FILES_LENGTH - files.length);

    for (const file of list) {
      const reader = new FileReader();

      if (file.size / 1024 / 1024 > MAX_FILE_SIZE) {
        sizeExceeds.push(file.name);
      } else {
        reader.onload = () => {
          const dataURL = reader.result as string;

          setImages((s) => s.concat(dataURL));
          setFiles((s) => s.concat(file));
        };

        reader.readAsDataURL(file);
      }

      if (sizeExceeds.length) {
        dispatch(
          showNotification({
            message: "The image size is max 5 mb",
            appearance: "error",
          }),
        );
      }
    }

    if (ref.current?.value) {
      ref.current.value = "";
    }
  };

  const removeImage = (ind: number) => {
    setImages((s) => s.filter((i, index) => ind !== index));
    setFiles((s) => s.filter((i, index) => ind !== index));
  };

  const [message, setMessage] = useState("");

  const lead = useSelector(PayPerLeadSelectors.getLead());
  const account = useSelector(getActiveAccount());

  const dispute = () => {
    if (account?.id && lead?.id) {
      dispatch(
        PayPerLeadActions.disputeLead.request({
          message,
          files,
          lead_id: lead?.id,
        }),
      );
    }

    onClose();
  };

  return (
    <div className="pay-per-lead-dispute-modal">
      <div className="pay-per-lead-dispute-modal-header">{heading}</div>
      <div className="pay-per-lead-dispute-modal-content">{content}</div>

      <Textarea
        value={message}
        onChange={(e) => setMessage((e.target as HTMLTextAreaElement).value?.slice(0, 250))}
        placeholder="Tell us why this lead doesn't fit your request (Optional)"
      />

      <Images list={images} onRemove={removeImage} />

      <Button
        buttonStyle="link"
        className="add-photos"
        onClick={handleClick}
        disabled={images.length >= MAX_FILES_LENGTH}
      >
        <Icon type="image" />
        Add photo(s)
      </Button>

      <input
        multiple
        accept="image/png, image/jpg, image/jpeg"
        ref={ref}
        onChange={addImage}
        style={{ display: "none" }}
        type="file"
      />

      <div className="pay-per-lead-dispute-buttons">
        <div className="count">
          {message.length}/{MAX_LENGTH}
        </div>

        <Button buttonStyle="blue-bordered" onClick={onClose}>
          Cancel
        </Button>

        <Button buttonStyle="accent-green" onClick={dispute} disabled={message.length > 250}>
          Dispute
        </Button>
      </div>
    </div>
  );
};
export default ModalDisputeLead;
